import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	createFolder,
	deleteDocument,
	deleteFolder,
	editDocument,
	editFolder,
	getDocumentList,
	getFolders,
	getVersionList,
	restoreDocument,
	restoreFolder,
	updateDocument,
	updateDocumentVersion,
	uploadDocument,
} from "./folders.thunks";
import { IDocument, IFolder, IFolders } from "./types";
import { OrganizationDocumentResponse } from "libs/apiClient/data-contracts";

interface FoldersState {
	folders: IFolders;
	documentList: OrganizationDocumentResponse[];
	newVersion: OrganizationDocumentResponse | null;
	versionMode: boolean;
	versionList: OrganizationDocumentResponse[];
}

const initialState: FoldersState = {
	folders: {
		basic: [],
		additional: [],
	},
	documentList: [],
	newVersion: null,
	versionMode: false,
	versionList: [],
};

const foldersSlice = createSlice({
	name: "foldersSlice",
	initialState,
	reducers: {
		ChangeVersionMode(state, action: PayloadAction<boolean>) {
			state.versionMode = action.payload;
		},
		UpdateDocument(state, action: PayloadAction<number>) {
			if (!state.newVersion) return;
			const targetIndex = state.documentList.findIndex((document) => document.id === action.payload);
			state.documentList[targetIndex] = state.newVersion;
			state.newVersion = null;
		},
		clearVersionList(state) {
			state.versionList = [];
		},
	},

	extraReducers: (builder) => {
		builder.addCase(getFolders.fulfilled, (state, action: PayloadAction<IFolder[]>) => {
			state.folders = {
				basic: [],
				additional: [],
			};
			action.payload.forEach((folder) => {
				if (folder.base) state.folders.basic.push(folder);
				else state.folders.additional.push(folder);
			});
		});
		builder.addCase(getDocumentList.fulfilled, (state, action: PayloadAction<IDocument[]>) => {
			state.documentList = action.payload;
		});
		builder.addCase(
			uploadDocument.fulfilled,
			(state, action: PayloadAction<{ response: IDocument; handler: boolean }>) => {
				if (action.payload.handler) state.documentList.unshift(action.payload.response);
			},
		);
		builder.addCase(getVersionList.fulfilled, (state, action: PayloadAction<IDocument[]>) => {
			state.versionList = action.payload;
		});
		builder.addCase(updateDocument.fulfilled, (state, action) => {
			const documentIndex = state.documentList.findIndex((document) => document.id === action.payload.docId);
			state.documentList[documentIndex] = action.payload.response;
		});
		builder.addCase(
			updateDocumentVersion.fulfilled,
			(state, action: PayloadAction<{ targetId: number; response: IDocument }>) => {
				const versionIndex = state.versionList.findIndex((document) => document.default);
				state.versionList[versionIndex].default = false;
				state.versionList = state.versionList.filter((document) => document.id !== action.payload.response.id);
				state.versionList.unshift(action.payload.response);

				state.newVersion = action.payload.response;
			},
		);
		builder.addCase(createFolder.fulfilled, (state, action: PayloadAction<IFolder>) => {
			state.folders.additional.push(action.payload);
		});
		builder.addCase(editFolder.fulfilled, (state, action: PayloadAction<IFolder>) => {
			const folderIndex = state.folders.additional.findIndex((folder) => folder.id === action.payload.id);
			state.folders.additional[folderIndex] = action.payload;
		});
		builder.addCase(editDocument.fulfilled, (state, action: PayloadAction<IDocument>) => {
			const docIndex = state.documentList.findIndex((document) => document.id === action.payload.id);
			state.documentList[docIndex] = action.payload;
		});
		builder.addCase(deleteFolder.fulfilled, (state, action: PayloadAction<any>) => {
			state.folders.additional = state.folders.additional.filter((folder) => folder.id !== action.payload);
		});
		builder.addCase(deleteDocument.fulfilled, (state, action: PayloadAction<any>) => {
			state.documentList = state.documentList.filter((document) => document.id !== action.payload);
		});
		builder.addCase(restoreFolder.fulfilled, (state, action: PayloadAction<any>) => {
			state.folders.additional = state.folders.additional.filter((folder) => folder.id !== action.payload);
		});
		builder.addCase(restoreDocument.fulfilled, (state, action: PayloadAction<any>) => {
			state.documentList = state.documentList.filter((document) => document.id !== action.payload);
			// archived: false и она пустая, тогда папку удаляем
		});
	},
});

export const { ChangeVersionMode, UpdateDocument, clearVersionList } = foldersSlice.actions;
export default foldersSlice.reducer;
